import React, { useState, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

interface Deck {
  id: number;
  public_id: string;
  title: string;
  slug?: string;
  public?: boolean;
}

interface User {
  id: number;
  slug?: string;
}

interface DeckSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deck: Deck;
  user: User;
  onUpdate: (deck: Deck) => void;
  onSlugChange?: (newSlug: string) => void;
}

export const DeckSettings: React.FC<DeckSettingsProps> = ({
  open,
  onOpenChange,
  deck,
  user,
  onUpdate,
  onSlugChange
}) => {
  const [title, setTitle] = useState(deck.title || '');
  const [slug, setSlug] = useState(deck.slug || '');
  const [isPublic, setIsPublic] = useState(deck.public || false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitle(deck.title || '');
    setSlug(deck.slug || '');
    setIsPublic(deck.public || false);
    setError(null);
  }, [deck, open]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (slug && !/^[a-z0-9\-_]+$/.test(slug)) {
      setError('Slug can only contain lowercase letters, numbers, hyphens, and underscores');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`/api/v1/decks/${deck.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deck: {
            title: title.trim(),
            slug: slug || null,
            public: isPublic
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        onUpdate(data.deck);

        if (slug && slug !== deck.slug && onSlugChange) {
          onSlugChange(slug);
        }

        onOpenChange(false);
      } else {
        const data = await response.json();
        setError(data.errors?.[0] || 'Failed to update deck settings');
      }
    } catch (err) {
      setError('Failed to update deck settings');
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = user.slug && slug
    ? `${window.location.origin}/${user.slug}/${slug}`
    : null;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[9999] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-gray-800 p-6 shadow-xl border border-gray-700 z-[10000] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]">
          <Dialog.Title className="text-lg font-semibold text-white mb-4">
            Deck Settings
          </Dialog.Title>

          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium text-gray-300 block mb-2">
                Deck Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="My Awesome Deck"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label className="text-sm font-medium text-gray-300">Public Deck</label>
                <p className="text-xs text-gray-400 mt-1">
                  Allow anyone with the link to view this deck
                </p>
              </div>
              <button
                onClick={() => setIsPublic(!isPublic)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isPublic ? 'bg-blue-600' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isPublic ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-300 block mb-2">
                Custom Slug
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder="my-awesome-deck"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                Lowercase letters, numbers, hyphens, and underscores only
              </p>
            </div>

            {publicUrl && isPublic && (
              <div className="bg-gray-700/50 rounded-lg p-3">
                <label className="text-xs font-medium text-gray-400 block mb-1">
                  Public URL
                </label>
                <code className="text-sm text-blue-400 break-all">{publicUrl}</code>
              </div>
            )}

            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Dialog.Close asChild>
              <button
                className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-600 rounded-lg hover:bg-gray-700 transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>

          <Dialog.Close asChild>
            <button
              className="absolute right-3 top-3 rounded-lg p-1 text-gray-400 hover:text-gray-300 hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
